export type Currency = "BRL"

const currencySymbols: Record<Currency | string, string> = {
	BRL: "R$"
}

export const formatCurrency = (value: number, currency: string) => {
	if (isNaN(value)) {
		return ""
	}

	const symbol = currencySymbols[currency]
	if (!symbol) {
		return "Moeda não suportada"
	}

	const options = {
		style: "currency",
		currency
	}
	const formattedValue = new Intl.NumberFormat("pt-BR", options).format(value / 1000)

	return `Subtotal: ${formattedValue}`
}

export const convertCentsInReals = (value: number) => {
	if (isNaN(value)) {
		return ""
	}

	const options = {
		style: "currency",
		currency: "BRL"
	}

	const formattedValue = new Intl.NumberFormat("pt-BR", options).format(value / 100)

	return formattedValue
}

/**
 * Caps the integer part of a money input at 9 digits — the largest 9-digit
 * number is 999,999,999 (~999 million), the maximum value allowed in any
 * money/currency field across the app.
 */
const MAX_MONEY_INPUT_INTEGER_DIGITS = 9

/**
 * Maximum value in cents derived from the integer digit cap: 9 integer
 * digits + 2 decimal digits = 11 digits total (e.g. 99,999,999,99 -> 9
 * billion cents). Used by useMoneyInputCents to clamp appended digits.
 */
export const MAX_MONEY_INPUT_CENTS_VALUE = 10 ** (MAX_MONEY_INPUT_INTEGER_DIGITS + 2) - 1

/**
 * Strips any character that isn't a digit or "," (the only accepted decimal
 * separator, pt-BR convention) from a money input's raw value — blocks "-",
 * "+", "e"/"E" (scientific notation), "." and any other letter, which a bare
 * `type="number"` input would otherwise accept. "." is intentionally
 * dropped rather than converted to ",", so it produces no visible effect
 * when typed. Also caps the integer part at 9 digits (max ~999 million).
 * Use on every money/currency TextField's onChange instead of the raw value.
 */
export const sanitizeMoneyInputValue = (rawValue: string): string => {
	const digitsAndComma = rawValue.replace(/[^\d,]/g, "")

	const [integerPart, ...decimalParts] = digitsAndComma.split(",")

	const cappedIntegerPart = integerPart.slice(0, MAX_MONEY_INPUT_INTEGER_DIGITS)

	if (decimalParts.length === 0) {
		return cappedIntegerPart
	}

	return `${cappedIntegerPart},${decimalParts.join("")}`
}

/**
 * Converts a money input's displayed value (reais, comma as decimal
 * separator, e.g. "20,50") into cents (e.g. 2050). Returns undefined for an
 * empty/blank input instead of NaN, so callers can distinguish "no value"
 * from "zero".
 */
export const parseMoneyInputValueToCents = (valueInReais: string): number | undefined => {
	if (valueInReais.trim() === "") {
		return undefined
	}

	return Math.round(Number(valueInReais.replace(",", ".")) * 100)
}

/**
 * Converts cents into a money input's displayed value (reais, "." as
 * thousands separator and "," as decimal separator, always 2 decimal
 * places, e.g. 150032 -> "1.500,32", 2000 -> "20,00", 0 -> "0,00").
 * Returns "0,00" for undefined/null/NaN so the input never appears empty.
 */
export const formatCentsToMoneyInputValue = (valueInCents?: number | null): string => {
	const resolvedValueInCents = valueInCents ?? 0
	const safeValueInCents = isNaN(resolvedValueInCents) ? 0 : resolvedValueInCents

	return new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(safeValueInCents / 100)
}
